/**
 * FolderPicker — inline folder-picker UI for the SharedLocationsCard
 * pending-claim row + connected-clients edit affordance.
 *
 * Walks the bucket via `printAgent.listFolders` (Plan 47.02-05 Task 1),
 * filters file entries OUT of the visible list (folders only — files are
 * noise here), supports breadcrumb navigation + always-visible filter
 * input + 50-entry cap with "+N more" hint + filtered-empty + empty-bucket
 * (text-input) flows.
 *
 * Path normalization (canonical form): paths carry trailing slash
 * everywhere. Display + storage + selection all use `clients/mini-zali/`.
 *
 * Auth gates run server-side; the picker surfaces backend errors as an
 * inline error block (NOT a toast — DESIGN.md §15.4).
 *
 * Loading state: 5x `<Skeleton h-8 w-full>` rows, never a centred spinner.
 *
 * Microcopy locked: "Print files folder", "This folder will be created on
 * first upload.", "Filter folders…", etc. The plan bans the words
 * "prefix" / "key" from operator-facing UI — we say "folder" everywhere.
 *
 * BLU-368 Plan 47.02-05 Task 3.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, FolderClosed, Search, AlertTriangle, ExternalLink, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { trpc } from '@/lib/trpc';
import { FolderBreadcrumb } from './FolderBreadcrumb';

interface FolderPickerProps {
  locationId: string;
  clientOrganizationId: string;
  /** Initial prefix (canonical form, with trailing slash). Empty = root. */
  initialPrefix?: string;
  onConfirm: (prefix: string) => void;
  onCancel: () => void;
  /** Disable confirm while a parent mutation is in flight. */
  isMutating?: boolean;
  /** Override confirm-button label when set; otherwise computed dynamically. */
  confirmButtonLabel?: string;
  /**
   * Tencent-only. Browse THIS bucket instead of the junction/location default
   * bucket. Threaded straight into `listFolders`; when it changes the browse
   * state resets to the bucket root so the operator never lands on a stale
   * folder from the previous bucket.
   */
  bucketOverride?: string;
  /**
   * Extra guard on the confirm button (in addition to `isMutating` / query
   * error). Used by the approve panel to block Approve while the chosen
   * override bucket name is malformed.
   */
  confirmDisabled?: boolean;
}

export function FolderPicker({
  locationId,
  clientOrganizationId,
  initialPrefix = '',
  onConfirm,
  onCancel,
  isMutating = false,
  bucketOverride,
  confirmDisabled = false,
}: FolderPickerProps) {
  // Path is the live "selected" folder. Always carries trailing slash when
  // non-empty. Splitting via `.split('/').filter(Boolean)` is safe in either
  // form (FolderBreadcrumb does the same).
  const [currentPrefix, setCurrentPrefix] = useState(initialPrefix);
  const [filterRaw, setFilterRaw] = useState('');
  const [filter, setFilter] = useState('');
  // Empty-bucket text-input state ("create folder via first upload"). Used
  // when the listFolders response has zero folder entries.
  const [newFolderName, setNewFolderName] = useState('');

  // Debounce filter (250ms) — DESIGN.md §15.5 typeahead spec.
  useEffect(() => {
    const t = window.setTimeout(() => setFilter(filterRaw.trim()), 250);
    return () => window.clearTimeout(t);
  }, [filterRaw]);

  // When the override bucket changes (operator picks a different bucket in the
  // approve panel), reset the browse state to the new bucket's root. Skip the
  // initial mount so the edit flow (no bucketOverride) keeps its `initialPrefix`.
  const bucketOverrideDidMount = useRef(false);
  useEffect(() => {
    if (!bucketOverrideDidMount.current) {
      bucketOverrideDidMount.current = true;
      return;
    }
    setCurrentPrefix('');
    setFilterRaw('');
    setFilter('');
    setNewFolderName('');
  }, [bucketOverride]);

  // Backend prefix arg expects no leading/trailing slash convention. The
  // backend's prefix validator allows trailing slash; we send canonical.
  const listQuery = trpc.printAgent.listFolders.useQuery(
    {
      locationId,
      clientOrganizationId,
      prefix: currentPrefix,
      // Tencent-only override; omitted (not sent) when unset so the default
      // junction/location bucket is browsed exactly as before.
      ...(bucketOverride ? { bucketOverride } : {}),
    },
    {
      // Always-fresh — the bucket may change between picks, but we don't
      // need to refetch on focus inside an inline expansion.
      refetchOnWindowFocus: false,
      // Don't retry forbidden — operator can't fix it from the picker UI.
      retry: (failureCount, err) => {
        const code = (err as { data?: { code?: string } } | undefined)?.data?.code;
        if (code === 'FORBIDDEN' || code === 'BAD_REQUEST') return false;
        return failureCount < 2;
      },
    },
  );

  // Folders only — files are noise in a folder picker.
  const folderEntries = useMemo(
    () => (listQuery.data?.entries ?? []).filter((e) => e.type === 'folder'),
    [listQuery.data?.entries],
  );

  // BLU-641 resolved-path preview. The PUT path composes
  // selected-folder / (customsFolder|printsFolder) / filename with empty
  // segments dropped (backend joinStorageKey order) — the owner-vault folders
  // are appended AFTER the folder chosen here, which is exactly the trap this
  // preview exists to expose. null until the folders have loaded.
  const [resolvedCustomPath, resolvedStandardPath] = useMemo(() => {
    const d = listQuery.data as
      | { customsFolder?: string | null; printsFolder?: string | null }
      | undefined;
    if (!d || d.customsFolder === undefined) return [null, null] as const;
    const resolve = (...segments: Array<string | null | undefined>) => {
      const joined = segments
        .map((s) => (s ?? '').replace(/^\/+|\/+$/g, ''))
        .filter(Boolean)
        .join('/');
      return joined ? `${joined}/<file>` : '<file>';
    };
    return [
      resolve(currentPrefix, d.customsFolder),
      resolve(currentPrefix, d.printsFolder),
    ] as const;
  }, [listQuery.data, currentPrefix]);

  // Filter case-insensitive substring match on the trimmed query.
  const visibleEntries = useMemo(() => {
    if (!filter) return folderEntries;
    const f = filter.toLowerCase();
    return folderEntries.filter((e) => e.name.toLowerCase().includes(f));
  }, [folderEntries, filter]);

  const isLoading = listQuery.isPending;
  const isError = listQuery.isError;
  // Translate any error mentioning "Prefix" → "Folder" so operator language
  // stays consistent (plan §"prefix"/"key" ban).
  const errorMessage = (() => {
    if (!listQuery.error) return null;
    const raw = listQuery.error.message ?? 'Could not load folders.';
    return raw.replace(/Prefix/g, 'Folder').replace(/prefix/g, 'folder');
  })();

  const navigateInto = (folderName: string) => {
    // Canonical form: append `<name>/` to currentPrefix.
    const next = currentPrefix
      ? `${currentPrefix}${folderName}/`
      : `${folderName}/`;
    setCurrentPrefix(next);
    setFilterRaw('');
    setFilter('');
  };

  const handleNavigate = (next: string) => {
    setCurrentPrefix(next);
    setFilterRaw('');
    setFilter('');
  };

  // Empty-bucket text input creates the folder NAME locally (no write API);
  // the actual folder is created lazily on first upload.
  const handleEmptyBucketCreate = () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) return;
    // Validate against the same regex the backend zod uses, minus the
    // empty-string allowance. Surface an inline message if invalid.
    if (!/^[A-Za-z0-9._-]+$/.test(trimmed)) {
      // Inline guidance — keep it simple.
      return;
    }
    const proposed = currentPrefix ? `${currentPrefix}${trimmed}/` : `${trimmed}/`;
    onConfirm(proposed);
  };

  // Dynamic action button label.
  const confirmLabel = (() => {
    if (!currentPrefix) return 'Approve at bucket root';
    return currentPrefix.length <= 30
      ? `Approve with ${currentPrefix}`
      : 'Approve with this folder';
  })();

  // Show empty-bucket-as-text-input panel only when not loading + no error
  // + zero folder entries. Files-only listings also hit this branch (noise).
  const showEmptyBucket = !isLoading && !isError && folderEntries.length === 0 && !filter;
  const showFilteredEmpty = !isLoading && !isError && folderEntries.length > 0 && visibleEntries.length === 0;
  const showTruncatedHint = (listQuery.data?.truncated ?? false) && !filter;

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">Print files folder</h4>
        {/* Header-level cancel/close affordance. */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onCancel}
          aria-label="Cancel folder selection"
          disabled={isMutating}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Breadcrumb */}
      <FolderBreadcrumb
        currentPrefix={currentPrefix}
        onNavigate={handleNavigate}
        disabled={isMutating}
      />

      {/* Filter input — always visible per plan. */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <Input
          type="text"
          placeholder="Filter folders…"
          value={filterRaw}
          onChange={(e) => setFilterRaw(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setFilterRaw('');
              setFilter('');
            }
          }}
          className="h-9 pl-8 text-sm"
          aria-label="Filter folders"
          disabled={isMutating}
        />
      </div>

      {/* Inline error block (NOT toast). */}
      {isError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-1">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-destructive">Could not load folders.</p>
              <p className="text-xs text-destructive/80">{errorMessage}</p>
              <Link
                to="/production/settings?tab=advanced#tencent-cos-accordion"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1"
              >
                Go to Production Settings · Advanced
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Loading skeleton — 5 rows, h-8. Never a centred spinner. */}
      {isLoading && (
        <div className="space-y-1.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      )}

      {/* Folder list */}
      {!isLoading && !isError && visibleEntries.length > 0 && (
        <ul role="list" className="rounded-md border divide-y bg-background max-h-[280px] overflow-y-auto">
          {visibleEntries.map((entry) => (
            <li key={entry.name}>
              <button
                type="button"
                className="w-full flex items-center gap-2 px-3 h-9 text-sm hover:bg-muted/50 focus-visible:bg-muted/50 transition-colors text-left"
                onClick={() => navigateInto(entry.name)}
                disabled={isMutating}
              >
                <FolderClosed className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                <span className="font-mono truncate flex-1">{entry.name}</span>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/70" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Filtered-empty (DESIGN.md §15.2) */}
      {showFilteredEmpty && (
        <div className="rounded-md border border-dashed p-4 text-center">
          <p className="text-sm text-muted-foreground">
            No folders match &ldquo;{filter}&rdquo;.
          </p>
          <button
            type="button"
            onClick={() => {
              setFilterRaw('');
              setFilter('');
            }}
            className="text-xs text-primary hover:underline mt-1"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* >50 entries — keep-typing hint. */}
      {showTruncatedHint && (
        <p className="text-xs text-muted-foreground/90">
          +{Math.max(0, folderEntries.length - visibleEntries.length)} more, keep typing to filter
        </p>
      )}

      {/* Empty-bucket panel: text input creates the folder via first upload. */}
      {showEmptyBucket && (
        <div className="rounded-md border border-dashed p-4 space-y-2 text-center">
          <FolderClosed className="h-8 w-8 text-muted-foreground/60 mx-auto" aria-hidden="true" />
          <p className="text-sm font-medium">No folders here yet.</p>
          <p className="text-xs text-muted-foreground">
            This folder will be created on first upload.
          </p>
          <div className="max-w-xs mx-auto pt-1">
            <Input
              type="text"
              placeholder="folder-name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  handleEmptyBucketCreate();
                }
              }}
              className="h-9 font-mono text-sm"
              disabled={isMutating}
            />
            <p className="text-[11px] text-muted-foreground/80 mt-1">
              Letters, digits, ., _, -
            </p>
          </div>
        </div>
      )}

      {/* Selected confirm line + action footer */}
      <div className="border-t pt-3 space-y-2">
        <p className="text-xs text-muted-foreground">
          Selected:{' '}
          <span className="font-mono text-foreground">
            {currentPrefix || '(bucket root)'}
          </span>
        </p>
        {resolvedCustomPath !== null && (
          <div className="text-xs text-muted-foreground space-y-0.5">
            {/* BLU-641: the FINAL paths prints resolve to — the selected folder
                PLUS the location owner's custom/standard folders appended after
                it. Makes a doubled folder (images/images/…) obvious at selection
                time instead of at the first print-miss. */}
            <p>
              Custom prints save to:{' '}
              <span className="font-mono text-foreground">{resolvedCustomPath}</span>
            </p>
            <p>
              Standard prints save to:{' '}
              <span className="font-mono text-foreground">{resolvedStandardPath}</span>
            </p>
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isMutating}
            className="h-9"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => onConfirm(currentPrefix)}
            disabled={isMutating || isError || confirmDisabled}
            className="h-9"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
